import { useState } from "react";
import axios from "axios";
import { Check, ArrowRight, Globe, Loader2 } from "lucide-react";
import {
  t,
  SECTORS_EN,
  SECTORS_TR,
  CLIENT_TYPES_EN,
  CLIENT_TYPES_TR,
  LANGUAGES_EN,
  LANGUAGES_TR,
  REF_OPTIONS_EN,
  REF_OPTIONS_TR,
} from "../lib/onboarding_i18n";
import { formatApiErrorDetail, API_BASE } from "../lib/api";
import Logo from "../components/Logo";
import FileUpload from "../components/FileUpload";

const EMPTY = {
  name: "", current_role: "", city: "", languages: [],
  sector: "", sub_specialties: [], typical_clients: [], typical_client_notes: "",
  what_they_bring: "", what_they_seek: "",
  desired_sectors: [], live_opportunity: "",
  whatsapp: "", email: "", linkedin: "",
  referrer: "", referrer_name: "", why_now: "",
  accept_rhythm: false, accept_rules: false, understand_compensation: false,
  open_to_organizer: false,
  photo_file_id: null, photo_preview_url: "",
  website: "", // honeypot
};

function Section({ number, title, sub, children }) {
  return (
    <section className="ts-card p-7 sm:p-10 space-y-7" data-testid={`section-${number}`}>
      <div className="flex items-baseline gap-4 pb-2 border-b border-[#C9A84C]/15">
        <span className="font-serif text-[#C9A84C] text-3xl sm:text-4xl">{number}</span>
        <div>
          <h2 className="font-serif text-xl sm:text-2xl text-[#F0EDE8]">{title}</h2>
          <div className="text-xs uppercase tracking-[0.3em] text-[#9A9590] mt-1">{sub}</div>
        </div>
      </div>
      <div className="space-y-6">{children}</div>
    </section>
  );
}

function Field({ label, hint, required, children }) {
  return (
    <div>
      <label className="overline">
        {label}{required && <span className="text-[#C9A84C]"> *</span>}
      </label>
      <div className="mt-2">{children}</div>
      {hint && <div className="text-xs text-[#9A9590]/80 mt-1.5">{hint}</div>}
    </div>
  );
}

function Chips({ options, labels, value, onChange, multi = true, dataTestid }) {
  const display = labels || options;
  const toggle = (v) => {
    if (multi) {
      onChange(value.includes(v) ? value.filter((x) => x !== v) : [...value, v]);
    } else {
      onChange(v);
    }
  };
  const isActive = (v) => (multi ? value.includes(v) : value === v);
  return (
    <div className="flex flex-wrap gap-2" data-testid={dataTestid}>
      {options.map((o, i) => (
        <button
          key={o}
          type="button"
          onClick={() => toggle(o)}
          className={`px-3 py-1.5 rounded-full text-xs uppercase tracking-widest transition ${
            isActive(o)
              ? "bg-[#C9A84C] text-[#0D0D0D]"
              : "border border-[#C9A84C]/25 text-[#9A9590] hover:border-[#C9A84C]/55 hover:text-[#C9A84C]"
          }`}
        >
          {display[i] || o}
        </button>
      ))}
    </div>
  );
}

function TagInput({ value, onChange, placeholder, dataTestid }) {
  const [input, setInput] = useState("");
  const add = () => {
    const v = input.trim();
    if (v && !value.includes(v)) onChange([...value, v]);
    setInput("");
  };
  return (
    <div>
      <div className="flex gap-2">
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }}
          placeholder={placeholder}
          className="ts-input flex-1"
          data-testid={dataTestid}
        />
        <button type="button" onClick={add} className="btn-ghost-gold px-4 rounded-md text-xs uppercase tracking-widest">
          Add
        </button>
      </div>
      {value.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-3">
          {value.map((v) => (
            <span key={v} className="text-[10px] uppercase tracking-widest text-[#C9A84C] border border-[#C9A84C]/30 rounded-full px-3 py-1 inline-flex items-center gap-2">
              {v}
              <button type="button" onClick={() => onChange(value.filter((x) => x !== v))} className="hover:text-red-400">×</button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

function CommitBox({ children, checked, onChange, dataTestid }) {
  // Render `**bold**` markers as gold-highlighted spans WITHOUT dangerouslySetInnerHTML.
  // Input is always our static i18n dictionary — never user input — but using JSX keeps
  // the code defensively safe and lint-clean.
  const parts = String(children).split(/(\*\*[^*]+\*\*)/g);
  return (
    <label className={`flex items-start gap-4 p-5 rounded-lg cursor-pointer transition border ${
      checked ? "border-[#C9A84C] bg-[rgba(201,168,76,0.05)]" : "border-[#C9A84C]/15 hover:border-[#C9A84C]/40"
    }`} data-testid={dataTestid}>
      <div className={`w-5 h-5 shrink-0 rounded mt-0.5 flex items-center justify-center transition ${
        checked ? "bg-[#C9A84C]" : "border border-[#C9A84C]/40"
      }`}>
        {checked && <Check size={14} className="text-[#0D0D0D]" strokeWidth={3} />}
      </div>
      <input type="checkbox" className="sr-only" checked={checked} onChange={(e) => onChange(e.target.checked)} />
      <div className="text-sm text-[#F0EDE8] leading-relaxed">
        {parts.map((p, idx) => {
          // `parts` is split from a static string on every render — items never reorder
          // and have no per-item state, so a positional key is correct. Composite avoids
          // collisions when the same fragment ("**" wrap) repeats.
          const key = `${idx}:${p}`;
          return p.startsWith("**") && p.endsWith("**") ? (
            <span key={key} className="text-[#C9A84C] font-medium">{p.slice(2, -2)}</span>
          ) : (
            <span key={key}>{p}</span>
          );
        })}
      </div>
    </label>
  );
}

export default function Onboarding() {
  const [lang, setLang] = useState("en");
  const [f, setF] = useState(EMPTY);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const i = t[lang];
  const sectorsLabels = lang === "tr" ? SECTORS_TR : SECTORS_EN;
  const clientsLabels = lang === "tr" ? CLIENT_TYPES_TR : CLIENT_TYPES_EN;
  const languagesLabels = lang === "tr" ? LANGUAGES_TR : LANGUAGES_EN;
  const refLabels = lang === "tr" ? REF_OPTIONS_TR : REF_OPTIONS_EN;

  const upd = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!f.name || !f.sector || !f.what_they_bring || !f.what_they_seek || !f.whatsapp || !f.email) {
      setError(i.err_required); return;
    }
    if (!f.accept_rhythm || !f.accept_rules || !f.understand_compensation) {
      setError(i.err_required); return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...f,
        referrer: f.referrer + (f.referrer_name ? ` — ${f.referrer_name}` : ""),
      };
      delete payload.referrer_name;
      delete payload.photo_preview_url;
      await axios.post(`${API_BASE}/onboarding/submit`, payload);
      setDone(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (ex) {
      setError(formatApiErrorDetail(ex?.response?.data?.detail) || i.error_default);
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="min-h-screen bg-[#0D0D0D] flex items-center justify-center px-6 py-16">
        <div className="max-w-md text-center animate-fade-in" data-testid="onboarding-success">
          <div className="w-16 h-16 mx-auto rounded-full bg-[#C9A84C]/15 border border-[#C9A84C]/40 flex items-center justify-center mb-8">
            <Check size={28} className="text-[#C9A84C]" strokeWidth={2} />
          </div>
          <Logo size="md" />
          <h1 className="font-serif text-4xl text-[#F0EDE8] mt-10">{i.success.title}</h1>
          <p className="text-[#9A9590] mt-5 leading-relaxed">{i.success.body}</p>
          <p className="text-xs uppercase tracking-[0.3em] text-[#9A9590]/70 mt-10">{i.success.hint}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0D0D0D] text-[#F0EDE8]">
      {/* Topbar */}
      <header className="border-b border-[#C9A84C]/12 sticky top-0 z-30 bg-[#0D0D0D]/90 backdrop-blur-md">
        <div className="max-w-3xl mx-auto px-6 sm:px-10 py-4 flex items-center justify-between">
          <div className="font-serif text-[#C9A84C] uppercase tracking-[0.32em] text-xs sm:text-sm">
            The Synergy Table
          </div>
          <button
            onClick={() => setLang(lang === "en" ? "tr" : "en")}
            className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-[#9A9590] hover:text-[#C9A84C] transition border border-[#C9A84C]/25 px-3 py-1.5 rounded-full"
            data-testid="lang-toggle"
          >
            <Globe size={14} /> {i.nav.switch}
          </button>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-5 sm:px-10 py-12 sm:py-20 space-y-10">
        {/* Hero */}
        <div className="space-y-5">
          <div className="overline">{i.hero.kicker}</div>
          <h1 className="font-serif text-4xl sm:text-5xl tracking-tight leading-tight">{i.hero.title}</h1>
          <p className="text-[#9A9590] leading-relaxed max-w-xl">{i.hero.lead}</p>
          <div className="flex flex-wrap gap-3 text-[10px] uppercase tracking-[0.3em] text-[#9A9590] pt-3">
            {i.hero.meta.map((m, idx) => (
              <span key={m} className="inline-flex items-center gap-2">
                {idx > 0 && <span className="w-1 h-1 rounded-full bg-[#C9A84C]" />}
                {m}
              </span>
            ))}
          </div>
        </div>

        <form onSubmit={submit} className="space-y-8" data-testid="onboarding-form">
          {/* honeypot */}
          <input type="text" name="website" autoComplete="off" tabIndex={-1}
            value={f.website} onChange={(e) => upd("website", e.target.value)}
            style={{ position: "absolute", left: "-9999px", height: 0, width: 0 }}
          />

          {/* Section 01 — Identity */}
          <Section number={i.sections.identity.number} title={i.sections.identity.title} sub={i.sections.identity.sub}>
            <Field label={i.f.photo} hint={i.f.photo_h}>
              <FileUpload
                endpoint="/uploads/onboarding-photo"
                public
                accept="image/jpeg,image/png,image/webp"
                maxBytes={5 * 1024 * 1024}
                previewUrl={f.photo_preview_url}
                variant="avatar"
                onUploaded={(rec) => {
                  upd("photo_file_id", rec.id);
                  upd("photo_preview_url", `/api/uploads/files/${rec.id}`);
                }}
                dataTestid="f-photo"
              />
            </Field>
            <Field label={i.f.name} required>
              <input className="ts-input" required value={f.name} onChange={(e) => upd("name", e.target.value)} data-testid="f-name"/>
            </Field>
            <Field label={i.f.role} hint={i.f.role_h}>
              <input className="ts-input" value={f.current_role} onChange={(e) => upd("current_role", e.target.value)} data-testid="f-role"/>
            </Field>
            <Field label={i.f.city}>
              <input className="ts-input" placeholder="Dubai" value={f.city} onChange={(e) => upd("city", e.target.value)} data-testid="f-city"/>
            </Field>
            <Field label={i.f.languages}>
              <Chips options={LANGUAGES_EN} labels={languagesLabels} value={f.languages} onChange={(v) => upd("languages", v)} dataTestid="f-languages"/>
            </Field>
          </Section>

          {/* Section 02 — Round Table */}
          <Section number={i.sections.table.number} title={i.sections.table.title} sub={i.sections.table.sub}>
            <Field label={i.f.sector} required>
              <Chips options={SECTORS_EN} labels={sectorsLabels} value={f.sector} onChange={(v) => upd("sector", v)} multi={false} dataTestid="f-sector"/>
            </Field>
            <Field label={i.f.sub} hint={i.f.sub_h}>
              <TagInput value={f.sub_specialties} onChange={(v) => upd("sub_specialties", v)} placeholder="DIFC, Cross-border M&A…" dataTestid="f-sub"/>
            </Field>
            <Field label={i.f.clients}>
              <Chips options={CLIENT_TYPES_EN} labels={clientsLabels} value={f.typical_clients} onChange={(v) => upd("typical_clients", v)} dataTestid="f-clients"/>
            </Field>
            <Field label={i.f.client_notes}>
              <input className="ts-input" value={f.typical_client_notes} onChange={(e) => upd("typical_client_notes", e.target.value)} data-testid="f-client-notes"/>
            </Field>
            <Field label={i.f.bring} hint={i.f.bring_h} required>
              <textarea className="ts-input resize-none py-2 min-h-[110px]" required value={f.what_they_bring} onChange={(e) => upd("what_they_bring", e.target.value)} data-testid="f-bring"/>
            </Field>
            <Field label={i.f.seek} hint={i.f.seek_h} required>
              <textarea className="ts-input resize-none py-2 min-h-[110px]" required value={f.what_they_seek} onChange={(e) => upd("what_they_seek", e.target.value)} data-testid="f-seek"/>
            </Field>
          </Section>

          {/* Section 03 — Synergy */}
          <Section number={i.sections.synergy.number} title={i.sections.synergy.title} sub={i.sections.synergy.sub}>
            <Field label={i.f.desired} hint={i.f.desired_h}>
              <Chips options={SECTORS_EN} labels={sectorsLabels} value={f.desired_sectors} onChange={(v) => upd("desired_sectors", v)} dataTestid="f-desired"/>
            </Field>
            <Field label={i.f.live} hint={i.f.live_h}>
              <textarea className="ts-input resize-none py-2 min-h-[100px]" value={f.live_opportunity} onChange={(e) => upd("live_opportunity", e.target.value)} data-testid="f-live"/>
            </Field>
          </Section>

          {/* Section 04 — Contact */}
          <Section number={i.sections.contact.number} title={i.sections.contact.title} sub={i.sections.contact.sub}>
            <Field label={i.f.whatsapp} hint={i.f.whatsapp_h} required>
              <input className="ts-input" type="tel" required value={f.whatsapp} onChange={(e) => upd("whatsapp", e.target.value)} data-testid="f-whatsapp"/>
            </Field>
            <Field label={i.f.email} required>
              <input className="ts-input" type="email" required value={f.email} onChange={(e) => upd("email", e.target.value)} data-testid="f-email"/>
            </Field>
            <Field label={i.f.linkedin}>
              <input className="ts-input" placeholder="https://linkedin.com/in/…" value={f.linkedin} onChange={(e) => upd("linkedin", e.target.value)} data-testid="f-linkedin"/>
            </Field>
          </Section>

          {/* Section 05 — Personal */}
          <Section number={i.sections.personal.number} title={i.sections.personal.title} sub={i.sections.personal.sub}>
            <Field label={i.f.referrer}>
              <Chips options={REF_OPTIONS_EN} labels={refLabels} value={f.referrer} onChange={(v) => upd("referrer", v)} multi={false} dataTestid="f-referrer"/>
            </Field>
            {f.referrer === REF_OPTIONS_EN[0] && (
              <Field label={i.f.ref_who}>
                <input className="ts-input" value={f.referrer_name} onChange={(e) => upd("referrer_name", e.target.value)} data-testid="f-referrer-name"/>
              </Field>
            )}
            <Field label={i.f.why} hint={i.f.why_h}>
              <textarea className="ts-input resize-none py-2 min-h-[100px]" value={f.why_now} onChange={(e) => upd("why_now", e.target.value)} data-testid="f-why"/>
            </Field>
          </Section>

          {/* Section 06 — Commitments */}
          <Section number={i.sections.commit.number} title={i.sections.commit.title} sub={i.sections.commit.sub}>
            <CommitBox checked={f.accept_rhythm} onChange={(v) => upd("accept_rhythm", v)} dataTestid="c-rhythm">
              {i.commit.rhythm}
            </CommitBox>
            <CommitBox checked={f.accept_rules} onChange={(v) => upd("accept_rules", v)} dataTestid="c-rules">
              {i.commit.rules}
            </CommitBox>
            <CommitBox checked={f.understand_compensation} onChange={(v) => upd("understand_compensation", v)} dataTestid="c-comp">
              {i.commit.comp}
            </CommitBox>
            <CommitBox checked={f.open_to_organizer} onChange={(v) => upd("open_to_organizer", v)} dataTestid="c-org">
              {i.commit.organizer}
            </CommitBox>
          </Section>

          {error && (
            <div className="text-sm text-red-400/90 border-l-2 border-red-500/60 pl-3 py-1" data-testid="onboarding-error">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={submitting}
            className="btn-gold w-full py-4 rounded-md inline-flex items-center justify-center gap-3 text-base disabled:opacity-70"
            data-testid="onboarding-submit"
          >
            {submitting ? <Loader2 size={18} className="animate-spin"/> : null}
            {submitting ? i.submitting : i.submit}
            {!submitting && <ArrowRight size={16} />}
          </button>

          <p className="text-[10px] uppercase tracking-[0.3em] text-[#9A9590]/70 text-center pt-4">
            The Synergy Table · Dubai · Confidential / Gizli
          </p>
        </form>
      </main>
    </div>
  );
}
